import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Users2 } from "lucide-react";
import MergeDialog from "@/components/admin/instructors/MergeDialog";
import { toast } from "sonner";

type Suggestion = {
  primary_id: string; duplicate_id: string;
  primary_name: string; duplicate_name: string;
  confidence: number; reason: string;
};

export default function PossibleDuplicatesCard() {
  const [rows, setRows] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [merge, setMerge] = useState<{ primary: string; duplicate: string } | null>(null);

  const load = async () => {
    setLoading(true);
    const { data } = await (supabase as any).rpc("admin_suggest_instructor_duplicates", { _limit: 15 });
    setRows((data ?? []) as Suggestion[]);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const ignore = async (s: Suggestion) => {
    const { error } = await (supabase as any).rpc("ignore_duplicate_pair", {
      _entity_type: "instructor", _a: s.primary_id, _b: s.duplicate_id, _reason: "Not a duplicate",
    });
    if (error) return toast.error(error.message);
    toast.success("Won't suggest this pair again.");
    setRows((rs) => rs.filter((r) => !(r.primary_id === s.primary_id && r.duplicate_id === s.duplicate_id)));
  };

  if (!loading && rows.length === 0) return null;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Users2 className="h-4 w-4" /> Possible Duplicate Instructors
            <span className="text-xs text-muted-foreground font-normal">({rows.length})</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-xs text-muted-foreground">Scanning…</div>
          ) : (
            <ul className="divide-y">
              {rows.map((s) => (
                <li key={`${s.primary_id}-${s.duplicate_id}`} className="py-2 flex items-center justify-between gap-2">
                  <div className="text-sm min-w-0">
                    <div className="truncate"><b>{s.primary_name}</b> ↔ <b>{s.duplicate_name}</b></div>
                    <div className="text-xs text-muted-foreground">{s.reason}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant={s.confidence >= 95 ? "default" : "outline"}>{s.confidence}%</Badge>
                    <Button size="sm" onClick={() => setMerge({ primary: s.primary_id, duplicate: s.duplicate_id })}>
                      Review & Merge
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => ignore(s)}>Ignore</Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
      <MergeDialog
        open={!!merge}
        onOpenChange={(o) => !o && setMerge(null)}
        primaryId={merge?.primary ?? null}
        duplicateId={merge?.duplicate ?? null}
        onMerged={() => { setMerge(null); load(); }}
      />
    </>
  );
}
